import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { CalendarDays } from 'lucide-react';
import { listMyVetAppointments, updateVetAppointmentStatus, type VetAppointment, type VetAppointmentStatus } from '../../api/vetAppointments';
import { MPL, MPL_FONT_DISPLAY } from '../../styles/mypetlive';
import { STATUS_META, AppointmentCard, LoadError, promptCancelReason, datetimeLocalNow } from './appointmentShared';

const card: React.CSSProperties = { background: '#fff', border: `1px solid ${MPL.border}`, borderRadius: 18, padding: 22 };
const inputStyle: React.CSSProperties = { border: `1.5px solid ${MPL.border}`, borderRadius: 10, padding: '8px 10px', font: 'inherit', background: '#fff' };

const ACTIVE: VetAppointmentStatus[] = ['requested', 'confirmed', 'rescheduled'];

export default function VetAppointmentsPanel() {
  const queryClient = useQueryClient();
  const [reschedFor, setReschedFor] = useState<string | null>(null);
  const [reschedAt, setReschedAt] = useState('');

  const apptsQ = useQuery({ queryKey: ['vet-appointments'], queryFn: () => listMyVetAppointments() });

  const mut = useMutation({
    mutationFn: (vars: { id: string; status: VetAppointmentStatus; scheduledAt?: string; vetNotes?: string; cancelReason?: string; addToHistory?: boolean }) =>
      updateVetAppointmentStatus(vars.id, { status: vars.status, scheduledAt: vars.scheduledAt, vetNotes: vars.vetNotes, cancelReason: vars.cancelReason, addToHistory: vars.addToHistory }),
    onSuccess: (data: any) => {
      // Antes esto se quedaba en un log del servidor: el vet veía "Cita
      // actualizada" sin enterarse de que no había cobrado las Patitas.
      if (data?.patitasSettlementFailed) {
        toast.error('Cita completada, pero la protectora no tenía saldo: las Patitas no se han cobrado. Contacta con ella.', { duration: 8000 });
      } else {
        toast.success(data?.clinicalRecordAdded ? 'Cita completada y añadida al pasaporte' : 'Cita actualizada');
      }
      setReschedFor(null); setReschedAt('');
      queryClient.invalidateQueries({ queryKey: ['vet-appointments'] });
    },
    onError: (e: any) => {
      const code = e?.response?.data?.error;
      // Un 409 significa que la tarjeta está obsoleta (alguien cambió la cita
      // por otro lado): decirlo y refrescar, en vez de un "no se pudo" a secas.
      if (code === 'invalid_transition') {
        toast.error('Esta cita ya ha cambiado de estado. Actualizamos la agenda.');
        queryClient.invalidateQueries({ queryKey: ['vet-appointments'] });
        return;
      }
      if (code === 'slot_taken' || code === 'reservation_inconsistent') {
        toast.error(code === 'slot_taken' ? 'Esa hora ya tiene otra cita. Elige otra.' : 'La agenda ha cambiado. La actualizamos: vuelve a intentarlo.');
        queryClient.invalidateQueries({ queryKey: ['vet-appointments'] });
        return;
      }
      toast.error(code === 'scheduled_at_required' ? 'Indica la nueva fecha'
        : code === 'invalid_date' ? 'Esa fecha no es válida o ya ha pasado.'
        : 'No se pudo actualizar');
    },
  });

  const complete = (a: VetAppointment) => {
    if (a.animalCode) {
      const note = window.prompt(`Completar cita de ${a.animalCode}.\nEscribe una nota clínica para añadirla al pasaporte (deja vacío para no añadir):`, a.reason || '');
      // Cancelar el prompt (null) aborta; texto (incl. vacío) completa.
      if (note === null) return;
      const addToHistory = note.trim().length > 0;
      mut.mutate({ id: a._id, status: 'completed', vetNotes: addToHistory ? note.trim() : undefined, addToHistory });
    } else {
      mut.mutate({ id: a._id, status: 'completed' });
    }
  };

  const all = apptsQ.data?.items || [];
  const active = all.filter(a => ACTIVE.includes(a.status));
  const past = all.filter(a => !ACTIVE.includes(a.status));

  const cancel = (a: VetAppointment) => {
    const extra = promptCancelReason(a.userId?.name || 'el cliente');
    if (!extra) return;
    mut.mutate({ id: a._id, status: 'cancelled', ...extra });
  };

  const renderActions = (a: VetAppointment) => {
    if (!ACTIVE.includes(a.status)) return [];
    if (mut.isPending) return [];
    const acts: any[] = [];
    if (a.status === 'requested') acts.push({ label: 'Confirmar', tone: 'primary', onClick: () => mut.mutate({ id: a._id, status: 'confirmed' }) });
    if (a.status === 'confirmed' || a.status === 'rescheduled') acts.push({ label: 'Marcar completada', tone: 'primary', onClick: () => complete(a) });
    acts.push({ label: reschedFor === a._id ? 'Cerrar' : 'Reprogramar', tone: 'neutral', onClick: () => { setReschedFor(reschedFor === a._id ? null : a._id); setReschedAt(''); } });
    acts.push({ label: 'Cancelar', tone: 'danger', onClick: () => cancel(a) });
    return acts;
  };

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <span style={{ width: 38, height: 38, borderRadius: 11, background: MPL.teal100, color: MPL.teal, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CalendarDays size={20} />
        </span>
        <div>
          <h3 style={{ fontFamily: MPL_FONT_DISPLAY, fontSize: 20, margin: 0 }}>Agenda de citas</h3>
          <p style={{ color: MPL.faint, fontSize: 13, margin: 0 }}>Solicitudes y citas de tus pacientes.</p>
        </div>
      </div>

      {apptsQ.isLoading ? (
        <div style={{ color: MPL.faint }}>Cargando…</div>
      ) : apptsQ.isError ? (
        // Sin esto, un fallo de red se leía como "no tienes solicitudes": el vet
        // podía dejar peticiones de cita sin contestar creyendo que no había.
        <LoadError
          title="No hemos podido cargar tu agenda."
          note="Puede ser un problema de conexión. No des por hecho que no tienes citas."
          onRetry={() => apptsQ.refetch()}
        />
      ) : all.length === 0 ? (
        <div style={{ color: MPL.faint, fontSize: 14 }}>Todavía no tienes solicitudes de cita.</div>
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          {active.length > 0 && (
            <div style={{ display: 'grid', gap: 10 }}>
              {active.map(a => (
                <AppointmentCard key={a._id} title={a.userId?.name || 'Cliente'} appt={a} meta={STATUS_META} actions={renderActions(a)}>
                  {reschedFor === a._id && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', background: MPL.bg, borderRadius: 10, padding: 10 }}>
                      <input type="datetime-local" min={datetimeLocalNow()} value={reschedAt} onChange={e => setReschedAt(e.target.value)} style={inputStyle} />
                      <button type="button" disabled={!reschedAt || mut.isPending}
                        onClick={() => mut.mutate({ id: a._id, status: 'rescheduled', scheduledAt: new Date(reschedAt).toISOString() })}
                        style={{ background: MPL.teal, color: '#fff', border: 0, borderRadius: 10, padding: '8px 14px', font: 'inherit', fontWeight: 800, fontSize: 13, cursor: 'pointer', opacity: reschedAt ? 1 : .6 }}>
                        Proponer nueva fecha
                      </button>
                    </div>
                  )}
                </AppointmentCard>
              ))}
            </div>
          )}

          {past.length > 0 && (
            <div>
              <div style={{ fontWeight: 800, fontSize: 13, color: MPL.muted, marginBottom: 8 }}>Historial</div>
              <div style={{ display: 'grid', gap: 10 }}>
                {past.map(a => (
                  <AppointmentCard key={a._id} title={a.userId?.name || 'Cliente'} appt={a} meta={STATUS_META} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
